import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaLightbulb } from 'react-icons/fa';
import { Section, HorizontalLine, IconWrapper } from '../common/Sections';
import { media } from '../../utils/media';
import MissionImage from '../../assets/images/mission.jpg';

/* Define centralized keyframe animations */
const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const MissionSection = () => {
  return (
    <Section id="mission">
      <Container>
        <ContentWrapper>
          {/* Text Content */}
          <TextContent>
            <TitleWrapper>
              <IconWrapper>
                <FaLightbulb />
              </IconWrapper>
              <Title>Our Mission</Title>
              <HorizontalLine />
            </TitleWrapper>
            <Description>
              We aim to empower every athlete by making high-quality sports footage accessible to all,
              while fostering a strong, connected local sports community. Your moments deserve to be
              celebrated, studied, and shared without barriers.
            </Description>
          </TextContent>

          {/* Image Content */}
          <ImageContent>
            <ImageWrapper>
              <ImageOverlay />
              <Image src={MissionImage} alt="Our Mission" />
            </ImageWrapper>
          </ImageContent>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default MissionSection;
/* ────────────── STYLED COMPONENTS ────────────── */

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  max-width: 1200px;
  position: relative;
  z-index: 2;

  ${media.tablet(`
    padding: 0 ${({ theme }) => theme.spacing.lg};
  `)}
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr; /* Mobile: single column */
  gap: ${({ theme }) => theme.spacing.xl};

  ${media.desktop(`
    grid-template-columns: 1fr 1fr; /* Desktop: two columns */
    gap: calc(${({ theme }) => theme.spacing.xl} * 1.2);
    align-items: center;
  `)}
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  animation: ${fadeInLeft} 1s ease-out;
  text-align: center;

  ${media.desktop(`
    text-align: left;
  `)}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  ${media.desktop(`
    align-items: flex-start;
  `)}
`;

const Title = styled.h2`
  font-size: clamp(1.4rem, 3.5vw, 2rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.title};
  margin: 0;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 50px;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.colors.secondary}
    );
    border-radius: 2px;
  }
`;

const Description = styled.p`
  font-size: clamp(0.9rem, 2vw, 1rem);
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.55;
  margin: 0;
`;

const ImageContent = styled.div`
  width: 100%;
  animation: ${fadeInRight} 1s ease-out;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 75%; /* Mobile aspect ratio */
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);

  ${media.desktop(`
    padding-bottom: 56.25%; /* 16:9 ratio on desktop */
    max-width: 400px;
    margin: 0 auto;
  `)}
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0) 100%);
  transition: background 0.3s ease;
  z-index: 1;
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease, filter 0.5s ease;

  &:hover {
    transform: scale(1.05);
    filter: brightness(1.05);
  }
`;

